import React, { useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
    currentPage: number;
    totalItems: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onPageSizeChange?: (size: number) => void;
    pageSizeOptions?: number[];
    className?: string;
    /** How far above the bottom of the screen (and the phone tab bar) the bar floats, in px */
    bottomOffset?: number;
}

const roundButton =
    'inline-flex h-8 min-w-[32px] items-center justify-center rounded-full px-1.5 text-[13px] tabular-nums transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40 disabled:cursor-not-allowed disabled:opacity-30';

export const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalItems,
    pageSize,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = [10, 25, 50],
    className = '',
    bottomOffset = 12,
}) => {
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const hidden = totalPages <= 1 && !onPageSizeChange;
    const floating = useFloating(!hidden);

    if (hidden) return null;

    const handlePrevious = () => {
        if (currentPage > 1) {
            onPageChange(currentPage - 1);
        }
    };

    const handleNext = () => {
        if (currentPage < totalPages) {
            onPageChange(currentPage + 1);
        }
    };

    const pageList = () => {
        const pages: (number | string)[] = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages + 2) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);

            let startPage = Math.max(2, currentPage - 1);
            let endPage = Math.min(totalPages - 1, currentPage + 1);

            if (currentPage <= 3) {
                endPage = Math.min(totalPages - 1, 4);
            }
            if (currentPage >= totalPages - 2) {
                startPage = Math.max(2, totalPages - 3);
            }

            if (startPage > 2) {
                pages.push('...');
            }

            for (let i = startPage; i <= endPage; i++) {
                pages.push(i);
            }

            if (endPage < totalPages - 1) {
                pages.push('...');
            }

            pages.push(totalPages);
        }

        return pages;
    };

    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);
    const range = `${startItem.toLocaleString()}–${endItem.toLocaleString()} of ${totalItems.toLocaleString()}`;

    const prevButton = (className: string) => (
        <button
            type="button"
            onClick={handlePrevious}
            disabled={currentPage === 1}
            aria-label="Previous page"
            title="Previous"
            className={`${roundButton} text-neutral-500 hover:bg-neutral-100 hover:text-ink ${className}`}
        >
            <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
        </button>
    );

    const nextButton = (className: string) => (
        <button
            type="button"
            onClick={handleNext}
            disabled={currentPage === totalPages}
            aria-label="Next page"
            title="Next"
            className={`${roundButton} text-neutral-500 hover:bg-neutral-100 hover:text-ink ${className}`}
        >
            <ChevronRight className="h-4 w-4" strokeWidth={1.75} />
        </button>
    );

    return (
        <>
            <div
                ref={floating.bar}
                className={`sticky z-20 rounded-2xl border px-4 py-3 font-ui transition-[background-color,border-color,box-shadow] duration-200 motion-reduce:transition-none ${
                    floating.on
                        ? 'border-neutral-200 bg-white/95 shadow-[0_12px_32px_-12px_rgba(0,0,0,0.25)] backdrop-blur'
                        : 'border-transparent'
                } ${className}`}
                style={{ bottom: `calc(var(--app-tabbar-h, 0px) + ${bottomOffset}px)` }}
            >
                {/* Phones: prev / range / next */}
                <div className="flex items-center justify-between gap-2 sm:hidden">
                    {prevButton('h-10 w-10 ring-1 ring-inset ring-neutral-200')}
                    <p className="text-[12px] tabular-nums text-neutral-400">{range}</p>
                    {nextButton('h-10 w-10 ring-1 ring-inset ring-neutral-200')}
                </div>

                {/* Desktop: range and page size on the left, pages on the right */}
                <div className="hidden items-center justify-between gap-4 sm:flex">
                    <div className="flex items-center gap-3">
                        <p className="text-[12px] tabular-nums text-neutral-400">{range}</p>
                        {onPageSizeChange && (
                            <div className="relative">
                                <select
                                    value={pageSize}
                                    onChange={(e) => onPageSizeChange(Number(e.target.value))}
                                    aria-label="Rows per page"
                                    className="h-7 cursor-pointer appearance-none rounded-full border-0 bg-white bg-none py-0 pl-2.5 pr-7 text-[12px] tabular-nums text-neutral-600 ring-1 ring-inset ring-neutral-200 transition-colors hover:text-ink focus:outline-none focus:ring-2 focus:ring-primary-600/30"
                                >
                                    {pageSizeOptions.map(size => (
                                        <option key={size} value={size}>{size} / page</option>
                                    ))}
                                </select>
                                <ChevronDown
                                    className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400"
                                    strokeWidth={1.75}
                                />
                            </div>
                        )}
                    </div>

                    <nav className="flex items-center gap-0.5" aria-label="Pagination">
                        {prevButton('w-8')}
                        {pageList().map((p, idx) => {
                            if (p === '...') {
                                return (
                                    <span key={`ellipsis-${idx}`} className="w-6 select-none text-center text-[13px] text-neutral-300">
                                        …
                                    </span>
                                );
                            }
                            const pageNum = p as number;
                            const isActive = pageNum === currentPage;
                            return (
                                <button
                                    type="button"
                                    key={pageNum}
                                    onClick={() => onPageChange(pageNum)}
                                    aria-current={isActive ? 'page' : undefined}
                                    className={`${roundButton} ${
                                        isActive
                                            ? 'bg-ink font-medium text-white'
                                            : 'text-neutral-500 hover:bg-neutral-100 hover:text-ink'
                                    }`}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}
                        {nextButton('w-8')}
                    </nav>
                </div>
            </div>
            {/* Where the bar sits when it is not floating */}
            <div ref={floating.home} aria-hidden="true" />
        </>
    );
};

/**
 * The bar is sticky to the bottom of the screen; it floats while its place in the
 * page is below the fold, which shows as its drawn bottom lying above that place.
 */
function useFloating(shown: boolean) {
    const bar = useRef<HTMLDivElement>(null);
    const home = useRef<HTMLDivElement>(null);
    const [on, setOn] = useState(false);

    // measured before paint, so a bar that mounts below the fold does not fade in
    useLayoutEffect(() => {
        if (!shown) return;
        let frame = 0;
        const measure = () => {
            frame = 0;
            if (!bar.current || !home.current) return;
            setOn(home.current.getBoundingClientRect().top - bar.current.getBoundingClientRect().bottom > 1);
        };
        const schedule = () => {
            if (!frame) frame = requestAnimationFrame(measure);
        };
        measure();
        window.addEventListener('scroll', schedule, { passive: true });
        window.addEventListener('resize', schedule);
        // rows loading or a page change moves the bar without a scroll
        const resized = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
        const page = bar.current?.parentElement;
        if (resized && page) resized.observe(page);
        return () => {
            window.removeEventListener('scroll', schedule);
            window.removeEventListener('resize', schedule);
            resized?.disconnect();
            if (frame) cancelAnimationFrame(frame);
        };
    }, [shown]);

    return { bar, home, on };
}
